import { z } from "zod";

/**
 * Business documents as FIRST-CLASS records — one record per issued document,
 * no matter how many orders or delivery notes it covers.
 *
 * ## THE SNAPSHOT RULE (locked, non-negotiable)
 *
 * A document is an **immutable snapshot of the moment it was issued**.
 * Everything needed to read, render or audit it lives INSIDE the record:
 * customer details, seller details, line items, amounts. We NEVER load those
 * by id at read time — if the customer later changes address or a product
 * price changes, the already-issued document must keep showing what it showed
 * when it was issued. That is what makes it a legal document rather than a
 * view over current data.
 *
 * **The only field that may change after issuance is `status`**
 * (open → paid / invoiced / cancelled). Nothing else is ever rewritten.
 *
 * Id fields that DO appear (`orderIds`, `deliveryNoteDocIds`, `productId`, …)
 * are relations for querying and reconciliation — never a way to fetch data
 * the document should already carry.
 *
 * ## Why the record exists at all
 *
 * Historically a document existed ONLY as an EZcount response payload copied
 * onto each covered order. A consolidated invoice therefore became N
 * duplicated copies with no single place to hold its state — which is why
 * paying one marked only one order, lists showed it N times at full amount,
 * and it could be charged twice. And the fact that matters most to the
 * business — *which delivery notes this invoice consolidates* — was sent to
 * EZcount as a comma-joined `parent` string and never stored on our side.
 *
 * ## Division of responsibility
 *
 * - **EZcount owns the LEGAL document**: numbering, PDF, VAT computation, tax
 *   authority reporting. Never recomputed here.
 * - **This record owns the BUSINESS SNAPSHOT and STATE**: what the document
 *   said, who it was for, what it covers, and whether it is still open.
 */

/**
 * A party (customer or seller) as it stood when the document was issued.
 * Snapshot — never re-read from the organization / store / client doc.
 */
export const DocumentPartySchema = z.object({
	name: z.string(),
	/**
	 * ח.פ / ע.מ.
	 *
	 * Recorded whenever we know it, even when the PRINTED document omits it:
	 * EZcount only receives `customer_crn` above the Israeli Tax Authority
	 * threshold (₪5,000), but that is a printing rule, not a statement about
	 * who the customer is. Gating the record on it too is what previously made
	 * a delivery note and its invoice disagree about the same customer.
	 */
	companyNumber: z.string().optional(),
	address: z.string().optional(),
	phone: z.string().optional(),
	email: z.string().optional(),
});

/**
 * One printed line of the document. Snapshot — the product may later be
 * renamed, repriced or deleted; the issued document must not change.
 */
export const DocumentLineSchema = z.object({
	/** Printed description, e.g. a product name or "תעודת משלוח 70097". */
	description: z.string(),
	quantity: z.number(),
	/** Integer agorot */
	unitPriceAgorot: z.number().int(),
	/** Integer agorot — the line total as printed. */
	totalAgorot: z.number().int(),
	/** EZcount VAT treatment: PRE = before VAT, INC = VAT included, NON = exempt. */
	vatType: z.enum(["PRE", "INC", "NON"]),
	sku: z.string().optional(),
	/** Relation only — NEVER loaded to render this document. */
	productId: z.string().optional(),
	/** Set when this line represents a whole delivery note (consolidated invoice). */
	deliveryNoteDocId: z.string().optional(),
});

export type TDocumentParty = z.infer<typeof DocumentPartySchema>;
export type TDocumentLine = z.infer<typeof DocumentLineSchema>;

const StoreDocumentBaseSchema = z.object({
	/** Doc id — the EZcount `doc_uuid` when the document exists in EZcount. */
	id: z.string().min(1),
	/** Legal document number as issued. */
	docNumber: z.string(),

	// ---- Snapshot: who ------------------------------------------------------
	/** The customer at issuance. Snapshot. */
	customer: DocumentPartySchema,
	/** The issuing business at issuance. Snapshot. */
	seller: DocumentPartySchema.optional(),

	// ---- Snapshot: what -----------------------------------------------------
	/** Printed lines. Empty for receipts (payment acknowledgments carry none). */
	lines: z.array(DocumentLineSchema),
	/** Free-text description as printed, e.g. "ריכוז תעודות משלוח". */
	description: z.string().optional(),

	// ---- Snapshot: how much -------------------------------------------------
	/**
	 * Canonical total in integer agorot (1 ILS = 100 agorot), computed ONCE at
	 * issuance — never re-parsed from EZcount's `price_total` display string.
	 *
	 * Zero is permitted only so anomalous legacy data can be backfilled and
	 * REPORTED rather than crashing the migration.
	 */
	totalAgorot: z.number().int().nonnegative(),
	/** VAT portion of the total, integer agorot. Snapshot. */
	vatAgorot: z.number().int().nonnegative(),
	currency: z.literal("ILS"),

	// ---- Relations (for querying — never for loading printed data) ----------
	/**
	 * Every order this document covers. Length 1 for a regular document,
	 * N for a consolidated invoice. This fact exists nowhere else.
	 */
	orderIds: z.array(z.string().min(1)),
	organizationId: z.string().optional(),
	clientId: z.string().optional(),
	billingAccountId: z.string().optional(),

	/** epoch millis — the document date as issued. */
	issueDate: z.number().int().positive(),

	/** Display pointers into EZcount — never a money or content source. */
	ez: z
		.object({
			docUuid: z.string().optional(),
			pdfLink: z.string().optional(),
			pdfLinkCopy: z.string().optional(),
		})
		.optional(),
	/**
	 * Our own rendered PDF, when it already existed at issuance (delivery
	 * notes). Invoices render theirs AFTER the record is written, so their
	 * records carry no `ourPdfLink` — filling it in later would be a
	 * post-issuance write, which the snapshot rule forbids. Where our rendered
	 * invoice PDF should live is decided when the screens move over.
	 */
	ourPdfLink: z.string().optional(),

	/** epoch millis */
	createdAt: z.number().int().positive(),
	/** epoch millis — only ever bumped by a STATUS change. */
	updatedAt: z.number().int().positive(),
	companyId: z.string().min(1),
	storeId: z.string().min(1),
});

/**
 * Invoice — two flavours in this business:
 *
 * - `tax_invoice` (חשבונית מס, EZcount 305) — issued by an admin over one or
 *   more DELIVERY NOTES and sent to the customer for payment. Starts `open`.
 * - `invoice_receipt` (חשבונית מס קבלה, EZcount 320) — a regular order paid
 *   online: the money is already in, so it is born `paid` and needs no
 *   separate receipt.
 */
export const InvoiceDocumentSchema = StoreDocumentBaseSchema.extend({
	type: z.literal("invoice"),
	invoiceType: z.enum(["tax_invoice", "invoice_receipt"]),
	/** consolidated = covers more than one delivery note (חשבונית מרוכזת). */
	kind: z.enum(["regular", "consolidated"]),
	/** The ONLY mutable field. "partially_paid" arrives with allocation (#124). */
	status: z.enum(["open", "paid", "cancelled"]),
	/**
	 * The delivery notes this invoice consolidates — the accounting truth.
	 * Today this is built as a comma-joined `parent` string for EZcount and
	 * then lost; here it is persisted. Empty for an `invoice_receipt`.
	 */
	deliveryNoteDocIds: z.array(z.string().min(1)),
	/** epoch millis — set together with status "paid". */
	paidAt: z.number().int().positive().optional(),
	/** id of the receipt document that settled this invoice. */
	receiptDocId: z.string().optional(),
	/** Israeli tax authority allocation number (מספר הקצאה). */
	allocationNumber: z.string().optional(),
	/** epoch millis */
	allocationDate: z.number().int().positive().optional(),
});

export const DeliveryNoteDocumentSchema = StoreDocumentBaseSchema.extend({
	type: z.literal("delivery_note"),
	/**
	 * The ONLY mutable field.
	 *
	 * `invoicing` is a RESERVATION held while an invoice is being issued over
	 * this note. It is taken BEFORE the EZcount call, so a second billing run
	 * is rejected before it can create a duplicate legal document — not merely
	 * blocked from saving afterwards.
	 */
	status: z.enum(["open", "invoicing", "invoiced", "cancelled"]),
	/** Set when an invoice consolidating this delivery note is issued. */
	invoiceDocId: z.string().optional(),
	/**
	 * Who holds (or held) the reservation. `operationId` is deterministic per
	 * billing operation — the same set of orders always yields the same id — so
	 * a retry after a timeout recognises its OWN claim and resumes instead of
	 * being rejected as a conflict.
	 */
	invoicingClaim: z
		.object({
			operationId: z.string().min(1),
			/** epoch millis */
			claimedAt: z.number().int().positive(),
		})
		.optional(),
});

export const ReceiptDocumentSchema = StoreDocumentBaseSchema.extend({
	type: z.literal("receipt"),
	/** The invoice this receipt acknowledges (EZcount `parent`). Required. */
	parentInvoiceDocId: z.string().min(1),
	/** The ONLY mutable field. */
	status: z.enum(["issued", "cancelled"]),
	/** How the money arrived, as printed. Snapshot. */
	paymentMethod: z.enum(["cash", "check", "bank_transfer", "credit_card"]),
	/** Ledger transaction this receipt was issued for (audit trail). */
	transactionId: z.string().optional(),
});

export const StoreDocumentSchema = z.discriminatedUnion("type", [
	InvoiceDocumentSchema,
	DeliveryNoteDocumentSchema,
	ReceiptDocumentSchema,
]);

export type TStoreDocument = z.infer<typeof StoreDocumentSchema>;
export type TInvoiceDocument = z.infer<typeof InvoiceDocumentSchema>;
export type TDeliveryNoteDocument = z.infer<typeof DeliveryNoteDocumentSchema>;
export type TReceiptDocument = z.infer<typeof ReceiptDocumentSchema>;
export type TStoreDocumentType = TStoreDocument["type"];
